// The relay's client edge from the browser (docs/08 "Client edge"): one WebSocket per
// device session, `hello` with the session token as the first frame (never in the URL),
// then conversations, run status and terminal sessions multiplexed over it. Frames are
// ocs-proto's CBOR in binary messages. A dropped socket reconnects with backoff and
// resubscribes with `after` cursors, so nothing already seen is fetched twice.
import type { ClientFrame, Subscription } from "../proto/frames";
import { FrameDecodeError } from "../proto/index";
import type { FrameCodec } from "./codec";
import { SubscriptionSet } from "./subscriptions";

export type RelayStatus = "idle" | "connecting" | "open" | "closed";

/** What the client needs from a WebSocket; the browser's satisfies it. */
export interface SocketLike {
  readonly readyState: number;
  binaryType: BinaryType;
  onopen: ((ev: Event) => void) | null;
  onmessage: ((ev: MessageEvent) => void) | null;
  onclose: ((ev: CloseEvent) => void) | null;
  onerror: ((ev: Event) => void) | null;
  send(data: string | ArrayBufferLike | ArrayBufferView): void;
  close(code?: number, reason?: string): void;
}

export type SocketFactory = (url: string) => SocketLike;

export interface RelayClientOptions {
  codec: FrameCodec;
  sessionToken: string;
  clientVersion?: string;
  url?: string;
  connect?: SocketFactory;
  /** Reconnect after an unexpected close; off for one-shot sockets and tests. */
  reconnect?: boolean;
  schedule?: (fn: () => void, ms: number) => unknown;
}

const OPEN = 1;
const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

/** The relay's client edge, on the same origin the UI was served from. */
export function relayUrl(loc: Pick<Location, "protocol" | "host"> = window.location): string {
  return `${loc.protocol === "https:" ? "wss" : "ws"}://${loc.host}/rtc/client`;
}

export class RelayClient {
  private readonly codec: FrameCodec;
  private readonly hello: ClientFrame;
  private readonly url: string;
  private readonly connectSocket: SocketFactory;
  private readonly reconnect: boolean;
  private readonly schedule: (fn: () => void, ms: number) => unknown;
  private readonly subs = new SubscriptionSet();
  private socket: SocketLike | null = null;
  private status: RelayStatus = "idle";
  private pending: ClientFrame[] = [];
  private attempts = 0;
  private closedByUs = false;
  private readonly frameCbs = new Set<(frame: ClientFrame) => void>();
  private readonly statusCbs = new Set<(status: RelayStatus) => void>();

  constructor(opts: RelayClientOptions) {
    this.codec = opts.codec;
    this.hello = { type: "hello", session_token: opts.sessionToken, client_version: opts.clientVersion ?? "web-0.1.0" };
    this.url = opts.url ?? relayUrl();
    this.connectSocket = opts.connect ?? ((url) => new WebSocket(url));
    this.reconnect = opts.reconnect ?? true;
    this.schedule = opts.schedule ?? ((fn, ms) => setTimeout(fn, ms));
  }

  connect(): void {
    if (this.socket) return;
    this.closedByUs = false;
    const socket = this.connectSocket(this.url);
    socket.binaryType = "arraybuffer";
    this.socket = socket;
    this.setStatus("connecting");
    socket.onopen = () => {
      this.attempts = 0;
      this.write(this.hello);
      for (const frame of this.subs.resubscribeFrames()) this.write(frame);
      const queued = this.pending;
      this.pending = [];
      for (const frame of queued) this.write(frame);
      this.setStatus("open");
    };
    socket.onmessage = (ev) => this.receive(ev.data);
    socket.onclose = () => this.dropped();
    socket.onerror = () => {
      // The browser fires close after error; dropped() handles it.
    };
  }

  /** Close for good; no reconnect. */
  close(): void {
    this.closedByUs = true;
    const socket = this.socket;
    this.socket = null;
    this.pending = [];
    if (socket) {
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      socket.close();
    }
    this.setStatus("closed");
  }

  /** Send now, or as soon as the socket is open and `hello` is out. */
  send(frame: ClientFrame): void {
    if (this.socket?.readyState === OPEN && this.status === "open") this.write(frame);
    else this.pending.push(frame);
  }

  /**
   * Subscribe, and keep it across reconnects; the return value unsubscribes. While the
   * socket is not open the subscription waits for `onopen`, which sends every live one.
   */
  subscribe(sub: Subscription): () => void {
    const id = this.subs.add(sub);
    if (this.status === "open") this.write(this.subs.subscribeFrame(sub));
    return () => {
      const removed = this.subs.remove(id);
      if (removed && this.status === "open") this.write(this.subs.unsubscribeFrame(removed));
    };
  }

  onFrame(cb: (frame: ClientFrame) => void): () => void {
    this.frameCbs.add(cb);
    return () => this.frameCbs.delete(cb);
  }

  onStatus(cb: (status: RelayStatus) => void): () => void {
    this.statusCbs.add(cb);
    cb(this.status);
    return () => this.statusCbs.delete(cb);
  }

  get currentStatus(): RelayStatus {
    return this.status;
  }

  private write(frame: ClientFrame): void {
    this.socket?.send(this.codec.encode(frame));
  }

  private receive(raw: unknown): void {
    let frame: ClientFrame;
    try {
      frame = this.codec.decode(raw);
    } catch (e) {
      if (e instanceof FrameDecodeError) return;
      throw e;
    }
    if (frame.type === "ping") {
      this.write({ type: "pong", nonce: frame.nonce });
      return;
    }
    this.subs.observe(frame);
    this.frameCbs.forEach((cb) => cb(frame));
  }

  private dropped(): void {
    if (this.closedByUs) return;
    this.socket = null;
    this.setStatus("closed");
    if (!this.reconnect) return;
    const delay = BACKOFF_MS[Math.min(this.attempts, BACKOFF_MS.length - 1)]!;
    this.attempts += 1;
    this.schedule(() => {
      if (!this.closedByUs && !this.socket) this.connect();
    }, delay);
  }

  private setStatus(status: RelayStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.statusCbs.forEach((cb) => cb(status));
  }
}
